import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '@envs/environment';
import { Router } from '@angular/router';

export interface User {
  id?: string;
  email?: string;
  name?: string;
  // Add other user properties as needed
}

export interface AuthState {
  token: string | null;
  isAuthenticated: boolean;
  otpVerified: boolean;
  user: User | null;
}

const initialState: AuthState = {
  token: null,
  isAuthenticated: false,
  otpVerified: false,
  user: null,
};

@Injectable({
  providedIn: 'root'
})
export class AuthStore {
  private state = new BehaviorSubject<AuthState>(this.getInitialState());

  // isAuthenticated is always derived from the token itself (presence + expiry),
  // never from the persisted boolean, so a stale or hand-edited localStorage
  // entry can no longer satisfy the guards
  isAuthenticated$ = this.state.pipe(
    map(state => ({
      isAuthenticated: this.isTokenValid(state.token),
      otpVerified: state.otpVerified
    }))
  );

  readonly token$ = this.state.pipe(map(state => state.token));
  readonly otpVerified$ = this.state.pipe(map(state => state.otpVerified));
  readonly user$ = this.state.pipe(map(state => state.user));
  
  // Get current state snapshot
  get currentState(): AuthState {
    return this.state.getValue();
  }

  constructor(private router: Router) {
    // Subscribe to state changes to persist in localStorage.
    // isAuthenticated is intentionally not persisted — it is always re-derived
    // from the token on load, so editing localStorage can't fake a session.
    this.state.subscribe(state => {
      if (typeof localStorage !== 'undefined') {
        const { isAuthenticated, ...persisted } = state;
        localStorage.setItem('auth-storage', JSON.stringify(persisted));
      }
    });
  }

  private getInitialState(): AuthState {
    if (typeof localStorage !== 'undefined') {
      const savedState = localStorage.getItem('auth-storage');
      if (savedState) {
        try {
          const parsed = JSON.parse(savedState);
          const tokenValid = this.isTokenValid(parsed.token ?? null);
          return {
            token: tokenValid ? parsed.token : null,
            isAuthenticated: tokenValid,
            otpVerified: tokenValid ? !!parsed.otpVerified : false,
            user: tokenValid ? parsed.user ?? null : null,
          };
        } catch {
          return initialState;
        }
      }
      return initialState;
    }
    return initialState;
  }

  /**
   * A token is considered valid when it exists and, if it is a JWT with an
   * `exp` claim, that expiry is still in the future. Non-JWT / undecodable
   * tokens fall back to a presence check (backend still has the final say).
   */
  isTokenValid(token: string | null): boolean {
    if (!token) {
      return false;
    }
    try {
      const parts = token.replace(/^Bearer\s+/i, '').split('.');
      if (parts.length !== 3) {
        return true;
      }
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      if (typeof payload.exp === 'number') {
        return payload.exp * 1000 > Date.now();
      }
      return true;
    } catch {
      return true;
    }
  }

  setToken(token: string): void {
    const newState = {
      ...this.currentState,
      token,
      isAuthenticated: !!token
    };
    this.state.next(newState);
  }

  setOtpVerified(verified: boolean): Observable<boolean> {
    const newState = {
      ...this.currentState,
      otpVerified: verified
    };
    this.state.next(newState);
    
    return new Observable<boolean>(observer => {
      observer.next(true);
      observer.complete();
    });
  }

  setUser(user: User): void {
    this.state.next({
      ...this.currentState,
      user
    });
  }

  logout(): Observable<boolean> {
    console.log('logout');

    // Set authentication and OTP verification to false
    this.state.next({
      ...this.currentState,
      isAuthenticated: false,
      otpVerified: false
    });

    // Clear cookies
    document.cookie = `${environment.tokenName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; Secure;`;
    document.cookie = `${environment.otpValidVar}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; Secure;`;
    
    // Reset state
    this.state.next(initialState);
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('auth-storage');
    }

    // Return an Observable instead of boolean
    return new Observable<boolean>(observer => {
      observer.next(true);
      observer.complete();
    });
  }

  // Helper method to select specific state slice
  select<K extends keyof AuthState>(key: K): Observable<AuthState[K]> {
    return this.state.pipe(map(state => state[key]));
  }
}